/*
  Mythic Haven precise search refinement
  --------------------------------------
  Shopify's storefront search is intentionally fuzzy. That is useful for broad
  discovery, but it caused platform queries such as "PS3" and edition queries
  such as "Far Cry 5" to surface adjacent products.

  This script keeps Horizon's native search template, filters, cards, pagination
  and infinite-scroll behaviour untouched. It only:
    1. hides fuzzy candidates that do not contain the requested phrase,
    2. understands common console abbreviations,
    3. suppresses duplicate product IDs if an infinite-scroll response repeats one,
    4. updates the visible result count.

  It is intentionally scoped to the search results-list only.
*/

(() => {
  const root = document.querySelector('results-list[data-mh-search-query]');
  if (!root) return;

  const rawQuery = root.dataset.mhSearchQuery || '';

  const normalise = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const compact = (value) => normalise(value).replace(/\s+/g, '');

  const query = normalise(rawQuery);
  const queryCompact = compact(rawQuery);

  if (!queryCompact) return;

  // Common customer shorthand. A PS3 search should match either an explicit
  // "PS3" tag/SKU or a full "PlayStation 3" title/tag, but never PS2/PS4/PS5.
  const aliasMap = {
    ps1: ['ps1', 'playstation1', 'playstation'],
    ps2: ['ps2', 'playstation2'],
    ps3: ['ps3', 'playstation3'],
    ps4: ['ps4', 'playstation4'],
    ps5: ['ps5', 'playstation5'],
    'xbox360': ['xbox360'],
    'xboxone': ['xboxone'],
    'xboxseriesx': ['xboxseriesx', 'seriesx'],
    'xboxseriess': ['xboxseriess', 'seriess'],
    '3ds': ['3ds', 'nintendo3ds'],
    'switch': ['switch', 'nintendoswitch']
  };

  const queryCandidates = aliasMap[queryCompact] || [queryCompact];
  const seen = new Set();

  const isPreciseMatch = (card) => {
    const haystackCompact = compact(card.dataset.mhSearchText || '');
    if (!haystackCompact) return false;
    return queryCandidates.some((candidate) => haystackCompact.includes(candidate));
  };

  const processCard = (card) => {
    if (!(card instanceof HTMLElement)) return;
    const id = card.dataset.productId || card.id;

    if (id && seen.has(id)) {
      card.remove();
      return;
    }

    if (id) seen.add(id);

    if (!isPreciseMatch(card)) {
      card.hidden = true;
      card.setAttribute('aria-hidden', 'true');
      card.dataset.mhSearchFiltered = 'true';
    } else {
      card.hidden = false;
      card.removeAttribute('aria-hidden');
      card.dataset.mhSearchFiltered = 'false';
    }
  };

  const updateSummary = () => {
    const cards = [...root.querySelectorAll('.product-grid__item[data-product-id]')];
    const visible = cards.filter((card) => !card.hidden).length;
    const summary = document.querySelector('[data-mh-search-summary]');

    if (summary) {
      if (visible > 0) {
        summary.textContent = `${visible} precise ${visible === 1 ? 'result' : 'results'} shown.`;
      } else {
        summary.textContent = 'No precise matches yet. Try the full game title, platform, franchise or edition.';
      }
    }

    // The native empty state only knows Shopify's fuzzy result count.
    // If our precise filter leaves zero visible cards, show a small inline note
    // without replacing or restructuring Horizon's search UI.
    let note = root.querySelector('[data-mh-precise-empty]');
    if (visible === 0 && cards.length > 0) {
      if (!note) {
        note = document.createElement('p');
        note.dataset.mhPreciseEmpty = 'true';
        note.className = 'mh-precise-search-empty';
        note.textContent = 'No exact matches in these results. Try a broader title or use the collection links above.';
        const grid = root.querySelector('#ResultsList');
        if (grid) grid.prepend(note);
      }
      note.hidden = false;
    } else if (note) {
      note.hidden = true;
    }
  };

  const processAll = () => {
    root.querySelectorAll('.product-grid__item[data-product-id]').forEach(processCard);
    updateSummary();
  };

  processAll();

  // Horizon may append more cards as the customer scrolls. Refine only the new
  // cards and keep the existing layout/infinite-scroll component untouched.
  const observer = new MutationObserver((mutations) => {
    let changed = false;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;

        if (node.matches?.('.product-grid__item[data-product-id]')) {
          processCard(node);
          changed = true;
        }

        node.querySelectorAll?.('.product-grid__item[data-product-id]').forEach((card) => {
          processCard(card);
          changed = true;
        });
      }
    }

    if (changed) updateSummary();
  });

  observer.observe(root, { childList: true, subtree: true });
})();
